import * as ExcelJS from 'exceljs';
import { format } from 'date-fns';
import type {
  CompletedServicingPayment,
  ServicingLoan,
  ServicingPayment
} from '@/types/servicing';

interface ServicingExportInput {
  loans: ServicingLoan[];
  filteredReceipts: CompletedServicingPayment[];
  paymentHistory: ServicingPayment[];
  dateLabel: string;
  statusLabel: string;
}

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF333333' }
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
  size: 10
};

function styleHeader(sheet: ExcelJS.Worksheet): void {
  sheet.getRow(1).eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
  });
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
}

function downloadWorkbook(buffer: ExcelJS.Buffer): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Servicing_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportServicingWorkbook(input: ServicingExportInput): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  const summary = workbook.addWorksheet('Loan Summary');
  summary.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'LO / LO2', key: 'loanOfficer', width: 24 },
    { header: 'Investor', key: 'investor', width: 24 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'Loan Amount', key: 'loanAmount', width: 16 },
    { header: 'First Payment', key: 'firstPaymentDate', width: 16 },
    { header: 'Investor Purchase', key: 'purchaseDate', width: 18 },
    { header: 'Payments Received', key: 'receivedCount', width: 18 },
    { header: 'Total Collected', key: 'totalCollected', width: 18 },
    { header: 'Principal Collected', key: 'principalCollected', width: 20 },
    { header: 'Interest Collected', key: 'interestCollected', width: 20 },
    { header: 'Escrow Collected', key: 'escrowCollected', width: 18 }
  ];
  styleHeader(summary);
  input.loans.forEach((loan) => summary.addRow({
    loanNumber: loan.loan_number,
    borrower: loan.borrower_name,
    loanOfficer: loan.loan_officer_display,
    investor: loan.investor_name,
    status: loan.status,
    loanAmount: loan.loan_amount,
    firstPaymentDate: loan.first_payment_date,
    purchaseDate: loan.investor_purchase_date,
    receivedCount: loan.received_payment_count,
    totalCollected: loan.total_received_payments,
    principalCollected: loan.total_principal_collected,
    interestCollected: loan.total_interest_collected,
    escrowCollected: loan.total_escrow_collected
  }));
  for (let column = 6; column <= 13; column += 1) {
    if (column !== 7 && column !== 8 && column !== 9) {
      summary.getColumn(column).numFmt = '$#,##0.00;[Red]($#,##0.00)';
    }
  }

  const receipts = workbook.addWorksheet('Filtered Receipt Activity');
  receipts.columns = [
    { header: 'Received Date', key: 'receivedDate', width: 18 },
    { header: 'Due Date', key: 'dueDate', width: 14 },
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'Payment #', key: 'paymentNumber', width: 12 },
    { header: 'Principal', key: 'principal', width: 16 },
    { header: 'Interest Income', key: 'interest', width: 18 },
    { header: 'Escrow', key: 'escrow', width: 16 },
    { header: 'Late Fee', key: 'lateFee', width: 14 },
    { header: 'Total', key: 'total', width: 16 },
    { header: 'Method', key: 'method', width: 16 },
    { header: 'Reference', key: 'reference', width: 18 }
  ];
  styleHeader(receipts);
  input.filteredReceipts.forEach(({ payment, loan }) => receipts.addRow({
    receivedDate: payment.payment_received_date,
    dueDate: payment.due_date,
    loanNumber: loan.loan_number,
    borrower: loan.borrower_name,
    paymentNumber: payment.payment_number,
    principal: payment.principal_received,
    interest: Number(payment.interest_received || 0) + Number(payment.late_fee || 0),
    escrow: payment.escrow_received,
    lateFee: payment.late_fee,
    total: payment.total_amount_received,
    method: payment.payment_method,
    reference: payment.reference_number
  }));
  for (let column = 6; column <= 10; column += 1) {
    receipts.getColumn(column).numFmt = '$#,##0.00;[Red]($#,##0.00)';
  }

  const history = workbook.addWorksheet('Full Payment History');
  history.columns = [
    { header: 'Loan #', key: 'loanNumber', width: 16 },
    { header: 'Borrower', key: 'borrower', width: 24 },
    { header: 'Payment #', key: 'paymentNumber', width: 12 },
    { header: 'Due Date', key: 'dueDate', width: 14 },
    { header: 'Received Date', key: 'receivedDate', width: 16 },
    { header: 'Status', key: 'status', width: 16 },
    { header: 'Principal Due', key: 'principalDue', width: 16 },
    { header: 'Interest Due', key: 'interestDue', width: 16 },
    { header: 'Escrow Due', key: 'escrowDue', width: 16 },
    { header: 'Total Due', key: 'totalDue', width: 16 },
    { header: 'Principal Received', key: 'principalReceived', width: 20 },
    { header: 'Interest Received', key: 'interestReceived', width: 20 },
    { header: 'Escrow Received', key: 'escrowReceived', width: 18 },
    { header: 'Total Received', key: 'totalReceived', width: 18 },
    { header: 'Reversed At', key: 'reversedAt', width: 22 },
    { header: 'Original Received Date', key: 'reversedReceivedDate', width: 22 },
    { header: 'Reversed Principal', key: 'reversedPrincipal', width: 20 },
    { header: 'Reversed Interest', key: 'reversedInterest', width: 20 },
    { header: 'Reversed Escrow', key: 'reversedEscrow', width: 18 },
    { header: 'Reversed Total', key: 'reversedTotal', width: 18 },
    { header: 'Reversal Reason', key: 'reversalReason', width: 20 },
    { header: 'Notes', key: 'notes', width: 30 }
  ];
  styleHeader(history);
  const loansById = new Map(input.loans.map((loan) => [loan.id, loan]));
  input.paymentHistory.forEach((payment) => {
    const loan = loansById.get(payment.servicing_loan_id);
    history.addRow({
      loanNumber: loan?.loan_number || '',
      borrower: loan?.borrower_name || '',
      paymentNumber: payment.payment_number,
      dueDate: payment.due_date,
      receivedDate: payment.payment_received_date,
      status: payment.status,
      principalDue: payment.principal_due,
      interestDue: payment.interest_due,
      escrowDue: payment.escrow_due,
      totalDue: payment.total_amount_due,
      principalReceived: payment.principal_received,
      interestReceived: payment.interest_received,
      escrowReceived: payment.escrow_received,
      totalReceived: payment.total_amount_received,
      reversedAt: payment.reversed_at,
      reversedReceivedDate: payment.reversed_payment_received_date,
      reversedPrincipal: payment.reversed_principal_received,
      reversedInterest: payment.reversed_interest_received,
      reversedEscrow: payment.reversed_escrow_received,
      reversedTotal: payment.reversed_total_amount_received,
      reversalReason: payment.reversal_reason,
      notes: payment.notes
    });
  });
  for (let column = 7; column <= 20; column += 1) {
    if (column !== 15 && column !== 16) {
      history.getColumn(column).numFmt = '$#,##0.00;[Red]($#,##0.00)';
    }
  }

  const instructions = workbook.addWorksheet('Export Info');
  instructions.columns = [
    { header: 'Field', key: 'field', width: 28 },
    { header: 'Value', key: 'value', width: 88 }
  ];
  styleHeader(instructions);
  instructions.addRow({ field: 'Status filter', value: input.statusLabel });
  instructions.addRow({ field: 'Receipt-date period', value: input.dateLabel });
  instructions.addRow({ field: 'Receipt rows', value: 'One row per monthly borrower payment received.' });
  instructions.addRow({ field: 'Income rule', value: 'Interest and late fees are income. Principal and escrow are balance-sheet clearing activity.' });
  instructions.addRow({ field: 'History', value: 'Full Payment History includes all schedule, receipt, waiver, bounce, and reversal metadata for the exported loans.' });

  downloadWorkbook(await workbook.xlsx.writeBuffer());
}

